// Captures the browser's PWA install prompt so the install page can trigger it
// on demand. `beforeinstallprompt` fires once, early, and only if we call
// preventDefault() — so this module attaches its listeners at import time
// (main.ts imports it up front) and stashes the event for later use.

type BeforeInstallPromptEvent = Event & {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  notify();
});

window.addEventListener('appinstalled', () => {
  deferred = null;
  notify();
});

/** True when the browser has offered a native install prompt we can trigger. */
export function canInstall(): boolean {
  return deferred !== null;
}

/** Subscribe to install-availability changes (prompt captured, or app installed). */
export function onInstallabilityChange(fn: () => void): void {
  listeners.add(fn);
}

/**
 * Trigger the native install prompt. Resolves to the user's choice, or null if
 * no prompt is available (iOS/Safari, or already installed).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferred) return null;
  deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  notify();
  return outcome;
}
